import React from 'react';
import { FetchJobResult, RefinitivJob, ValidateResult } from '../../../services/refinitivService';

/** "Waiting for the VM… / Running… chunk 3/12 / Done / Failed: …" for any Refinitiv job. */
export const JobStatusLine: React.FC<{ job: RefinitivJob | null; error: string | null; workerOnline?: boolean }> = ({ job, error, workerOnline }) => {
  if (error) return <p className="text-sm text-red-600 whitespace-pre-wrap">{error}</p>;
  if (!job) return null;
  if (job.status === 'queued') {
    return (
      <p className="text-sm text-gray-600">
        ⏳ Waiting for the Refinitiv worker on the VM…
        {workerOnline === false && <span className="text-amber-700"> The worker is offline: the job will run when the VM is back.</span>}
      </p>
    );
  }
  if (job.status === 'running') {
    const p = job.progress;
    return (
      <div className="text-sm text-gray-600">
        <p>🔄 Running against Refinitiv{p?.chunks ? ` — chunk ${p.chunk}/${p.chunks}` : '…'}</p>
        {p?.chunks ? (
          <div className="mt-1 h-1.5 bg-gray-200 rounded">
            <div className="h-1.5 bg-blue-600 rounded" style={{ width: `${Math.round(((p.chunk ?? 0) / p.chunks) * 100)}%` }} />
          </div>
        ) : null}
      </div>
    );
  }
  if (job.status === 'cancelled') return <p className="text-sm text-gray-500">Cancelled.</p>;
  return null;
};

export const ValidateTable: React.FC<{ result: ValidateResult }> = ({ result }) => (
  <div>
    <p className={`text-sm font-medium ${result.ok ? 'text-green-700' : 'text-red-700'}`}>
      {result.ok
        ? `✓ All ${result.rics.filter((r) => r.role !== 'expired').length} RICs the daily fetch requests answer.`
        : `✗ ${result.missing.length} RIC(s) don't answer: ${result.missing.join(', ')}`}
    </p>
    <table className="mt-2 w-full text-xs">
      <thead className="text-gray-500">
        <tr><th className="text-left py-1">RIC</th><th className="text-left">Role</th><th className="text-left">Month</th><th className="text-right">Price</th><th className="text-right">Date</th></tr>
      </thead>
      <tbody>
        {result.rics.map((r) => (
          <tr key={`${r.role}-${r.ric}`} className={r.ok ? '' : 'text-red-600'}>
            <td className="py-0.5 font-mono">{r.ok ? '✓' : '✗'} {r.ric}</td>
            <td>{r.role === 'expired' ? 'history' : r.role}</td>
            <td>{r.maturityMonth?.slice(0, 7) ?? ''}</td>
            <td className="text-right">{r.price ?? (r.rows != null ? `${r.rows} rows` : '')}</td>
            <td className="text-right">{r.date ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const FetchJobSummary: React.FC<{ result: FetchJobResult }> = ({ result }) => (
  <div className="text-sm">
    <p className="text-green-700 font-medium">✓ {result.pointsSaved} price points saved.</p>
    {result.summary && <p className="text-xs text-gray-500 mt-1">{result.summary}</p>}
    {result.log?.length ? (
      <details className="mt-1">
        <summary className="text-xs text-gray-500 cursor-pointer">Log</summary>
        <pre className="text-[11px] bg-gray-50 p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap">{result.log.join('\n')}</pre>
      </details>
    ) : null}
  </div>
);
